import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  message,
  Card,
  Empty,
  Spin
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  EyeOutlined
} from '@ant-design/icons';
import axios from 'axios';
import * as serverconfig from '../../serverconn';
import moment from 'moment';

const PatientList = ({ 
  pendingPickupId, 
  onAddPatient, 
  onEditPatient,
  visible,
  onClose,
  onPatientDeleted
}) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (visible && pendingPickupId) {
      loadPatients();
    }
  }, [visible, pendingPickupId]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${serverconfig.backendserverurl}/api/patients/?pending_sample_pickup=${pendingPickupId}`,
        {
          headers: {
            'Authorization': `Token ${localStorage.getItem('token')}`
          }
        }
      );
      setPatients(response.data);
    } catch (error) {
      console.error('Error loading patients:', error);
      message.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (patientId) => {
    Modal.confirm({
      title: 'Delete Patient',
      content: 'Are you sure you want to delete this patient?',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setDeleteLoading(true);
          await axios.delete(
            `${serverconfig.backendserverurl}/api/patients/${patientId}/`,
            {
              headers: {
                'Authorization': `Token ${localStorage.getItem('token')}`
              }
            }
          );
          message.success('Patient deleted successfully');
          loadPatients(); // Reload the list
          // Notify parent component to refresh data
          if (onPatientDeleted) {
            onPatientDeleted();
          }
        } catch (error) {
          console.error('Error deleting patient:', error);
          message.error('Failed to delete patient');
        } finally {
          setDeleteLoading(false);
        }
      }
    });
  };

  const columns = [
    {
      title: 'Names',
      dataIndex: 'names',
      key: 'names',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Barcode',
      dataIndex: 'barcode_display',
      key: 'barcode_display',
      render: (barcode_display) => {
        if (barcode_display) {
          return <Tag color="green">{barcode_display.bar_code}</Tag>;
        }
        return <Tag color="default">No barcode</Tag>;
      }
    },
    {
      title: 'Age',
      dataIndex: 'age',
      key: 'age',
      render: (age, record) => {
        const calculatedAge = record.age_in_years || age;
        return `${calculatedAge} years`;
      }
    },
    {
      title: 'Sex',
      dataIndex: 'sex',
      key: 'sex',
      render: (sex) => (
        <Tag color={sex === 'Male' ? 'blue' : 'pink'}>
          {sex}
        </Tag>
      )
    },
    {
      title: 'Nationality',
      dataIndex: 'nationality',
      key: 'nationality'
    },
    {
      title: 'Phone',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (phone) => phone || '-'
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => moment(date).format('DD/MM/YYYY HH:mm')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEditPatient(record)}
          >
            Edit
          </Button>
          <Button
            type="primary"
            danger
            size="small"
            icon={<DeleteOutlined />}
            loading={deleteLoading}
            onClick={() => handleDelete(record.id)}
          >
            Delete
          </Button>
        </Space>
      )
    }
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <UserOutlined style={{ marginRight: 8 }} />
            Patients for this Pickup
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onAddPatient}
          >
            Add Patient
          </Button>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width="95%"
      style={{ maxWidth: 1000 }}
    >
      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : patients.length === 0 ? (
          <Empty
            description="No patients found for this pickup"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={onAddPatient}>
              Add First Patient
            </Button>
          </Empty>
        ) : (
          <Table
            columns={columns}
            dataSource={patients}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} patients`
            }}
            size="small"
          />
        )}
      </Card>
    </Modal>
  );
};

export default PatientList;
